import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Check, Search, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import config from '../config';

const API = config.BACKEND_URL;

/**
 * Afficher l'UI de changement de secteur seulement s'il y a un vrai choix (≥ 2).
 * Le secteur principal (service_id) compte toujours dans le total.
 */
export function userCanSwitchSecteur(user) {
  if (!user) return false;
  const ids = new Set();
  const list = Array.isArray(user.secteurs_autorises) ? user.secteurs_autorises : [];
  list.forEach((id) => {
    if (id == null) return;
    const s = String(id).trim();
    if (s) ids.add(s);
  });
  if (user.service_id) {
    const sid = String(user.service_id).trim();
    if (sid) ids.add(sid);
  }
  return ids.size >= 2;
}

/**
 * Modal de confirmation / changement de secteur (≥ 2 secteurs).
 * Affiché à chaque login/refresh tant que hide_secteur_modal n'est pas true.
 * Le dropdown ne fait que préparer un choix ; Continuer applique et ferme.
 */
const SecteurSelectorModal = ({
  open,
  onClose,
  onSecteurSelected,
  token,
  updateUser,
  user,
  userId,
  onHidePreferenceSaved,
}) => {
  const [secteurs, setSecteurs] = useState([]);
  const [secteurActifId, setSecteurActifId] = useState(null);
  const [pendingSecteurId, setPendingSecteurId] = useState(null);
  const [hideForever, setHideForever] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && token) {
      setHideForever(false);
      fetchSecteursAutorises();
    }
  }, [open, token]);

  const fetchSecteursAutorises = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/secteurs-autorises?token=${token}`);
      const loaded = response.data.secteurs || [];
      setSecteurs(loaded);
      const actif =
        response.data.actif_secteur ||
        user?.actif_secteur ||
        user?.service_id ||
        null;
      setSecteurActifId(actif);
      setPendingSecteurId(actif);
    } catch (error) {
      console.error('Erreur lors du chargement des secteurs:', error);
      toast.error('Erreur lors du chargement des secteurs');
    } finally {
      setLoading(false);
    }
  };

  const secteurLabel = (secteurId) => {
    if (!secteurId) return '—';
    const s = secteurs.find((x) => x.id === secteurId);
    if (!s) return '—';
    return s.marketing?.display_name || s.name || '—';
  };

  const handleContinue = async () => {
    try {
      setSubmitting(true);
      const targetId = pendingSecteurId || secteurActifId;

      if (targetId && targetId !== secteurActifId) {
        await axios.post(
          `${API}/auth/change-actif-secteur?token=${token}`,
          { secteur_id: targetId }
        );
        if (updateUser) {
          updateUser({ actif_secteur: targetId });
        }
        const selectedSecteur = secteurs.find((s) => s.id === targetId);
        if (onSecteurSelected) {
          onSecteurSelected(selectedSecteur);
        }
        toast.success('Secteur actif modifié');
      }

      if (hideForever && userId) {
        const response = await axios.put(`${API}/preferences/${userId}`, {
          hide_secteur_modal: true,
        });
        if (onHidePreferenceSaved) {
          onHidePreferenceSaved(response.data);
        }
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la confirmation du secteur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation du secteur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Fermeture uniquement via Continuer
        if (!nextOpen) return;
      }}
    >
      <DialogContent
        className="max-w-md dark:bg-gray-800 [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold dark:text-white flex items-center gap-2">
            <Building2 className="text-[#f76f4b]" />
            Votre secteur
          </DialogTitle>
          <DialogDescription className="dark:text-gray-400 sr-only">
            Confirmer ou changer le secteur actif
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {loading ? (
            <div className="py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
              Chargement des secteurs...
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                Vous êtes actuellement sur le secteur{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {secteurLabel(secteurActifId)}
                </span>
              </p>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Changer de secteur :
                </label>
                <SecteurSelectorDropdown
                  user={user}
                  token={token}
                  updateUser={updateUser}
                  deferApply
                  pendingSecteurId={pendingSecteurId}
                  onPendingSecteurChange={setPendingSecteurId}
                  secteursOverride={secteurs}
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                <Checkbox
                  checked={hideForever}
                  onCheckedChange={(v) => setHideForever(v === true)}
                  disabled={submitting}
                />
                Ne plus afficher
              </label>

              <Button
                onClick={handleContinue}
                disabled={submitting || loading}
                className="w-full bg-[#f76f4b] hover:bg-[#e55a3a]"
              >
                {submitting ? 'Patientez…' : 'Continuer'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Sélecteur de secteur compact (sidebar ou modal).
 * deferApply=true : sélection locale uniquement (pas d'appel API).
 */
const SecteurSelectorDropdown = ({
  user,
  token,
  onSecteurChange,
  updateUser,
  deferApply = false,
  pendingSecteurId = null,
  onPendingSecteurChange,
  secteursOverride = null,
}) => {
  const [secteurs, setSecteurs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentSecteur, setCurrentSecteur] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const canSwitch = userCanSwitchSecteur(user);

  useEffect(() => {
    if (Array.isArray(secteursOverride) && secteursOverride.length > 0) {
      setSecteurs(secteursOverride);
      const actifId = deferApply
        ? (pendingSecteurId || user?.actif_secteur || user?.service_id)
        : (user?.actif_secteur || user?.service_id);
      const actif = secteursOverride.find((s) => s.id === actifId) || null;
      setCurrentSecteur(actif);
      setIsLoading(false);
      return;
    }
    if (canSwitch && token) {
      loadSecteurs();
    } else {
      setSecteurs([]);
      setCurrentSecteur(null);
      setIsLoading(false);
    }
  }, [
    canSwitch,
    token,
    user?.actif_secteur,
    user?.id,
    deferApply,
    pendingSecteurId,
    secteursOverride,
  ]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('.secteur-selector-dropdown')) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const loadSecteurs = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/auth/secteurs-autorises?token=${token}`);
      const loadedSecteurs = response.data.secteurs || [];
      setSecteurs(loadedSecteurs);
      const actifId = user?.actif_secteur || user?.service_id;
      if (actifId && loadedSecteurs.length > 0) {
        const actif = loadedSecteurs.find((s) => s.id === actifId);
        setCurrentSecteur(actif || null);
      }
    } catch (error) {
      console.error('Erreur chargement secteurs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeSecteur = async (secteurId) => {
    if (deferApply) {
      const selectedSecteur = secteurs.find((s) => s.id === secteurId);
      setCurrentSecteur(selectedSecteur || null);
      if (onPendingSecteurChange) {
        onPendingSecteurChange(secteurId);
      }
      setIsOpen(false);
      setSearchQuery('');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API}/auth/change-actif-secteur?token=${token}`,
        { secteur_id: secteurId }
      );
      const selectedSecteur = secteurs.find((s) => s.id === secteurId);
      setCurrentSecteur(selectedSecteur);

      if (updateUser) {
        updateUser({ actif_secteur: secteurId });
      }

      if (onSecteurChange) {
        onSecteurChange(selectedSecteur);
      }
      toast.success('Secteur actif modifié');
      setIsOpen(false);
      setSearchQuery('');
    } catch (error) {
      toast.error('Erreur lors du changement de secteur');
    } finally {
      setLoading(false);
    }
  };

  if (!canSwitch || (!isLoading && secteurs.length < 2)) return null;

  const getDisplayText = () => {
    if (isLoading) return 'Chargement...';
    if (currentSecteur) {
      return currentSecteur.marketing?.display_name || currentSecteur.name;
    }
    return 'Choisir un secteur';
  };

  const filteredSecteurs = secteurs.filter((secteur) => {
    if (!searchQuery) return true;
    const displayName = (secteur.marketing?.display_name || secteur.name).toLowerCase();
    const companyName = (secteur.company_name || '').toLowerCase();
    const search = searchQuery.toLowerCase();
    return displayName.includes(search) || companyName.includes(search);
  });

  return (
    <div className="relative secteur-selector-dropdown" data-testid="secteur-selector">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600"
      >
        <Building2 size={16} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <span className="flex-1 text-left truncate text-xs sm:text-sm">
          {getDisplayText()}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 sm:hidden"
            onClick={() => { setIsOpen(false); setSearchQuery(''); }}
          />

          <div className="fixed sm:absolute left-4 right-4 sm:left-0 sm:right-0 bottom-4 sm:bottom-auto sm:top-full sm:mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 z-[100] overflow-hidden max-h-[70vh] sm:max-h-80 flex flex-col">
            <div className="p-2 sm:p-2 border-b dark:border-gray-700 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un secteur..."
                  className="w-full pl-8 pr-3 py-2 sm:py-1.5 text-sm rounded border dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#f76f4b]"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredSecteurs.length === 0 ? (
                <div className="px-3 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {searchQuery ? 'Aucun secteur trouvé' : 'Aucun secteur disponible'}
                </div>
              ) : (
                filteredSecteurs.map((secteur) => (
                  <button
                    type="button"
                    key={secteur.id}
                    onClick={() => handleChangeSecteur(secteur.id)}
                    disabled={loading}
                    className={`
                      w-full px-3 py-3 sm:py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 
                      flex items-center justify-between transition-colors border-b dark:border-gray-700 last:border-b-0
                      ${currentSecteur?.id === secteur.id ? 'bg-gray-100 dark:bg-gray-700' : ''}
                    `}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium dark:text-white truncate">
                        {secteur.marketing?.display_name || secteur.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {secteur.company_name}
                      </div>
                    </div>
                    {currentSecteur?.id === secteur.id && (
                      <Check size={16} className="text-[#f76f4b] flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))
              )}
            </div>

            <div className="p-2 border-t dark:border-gray-700 sm:hidden flex-shrink-0">
              <button
                type="button"
                onClick={() => { setIsOpen(false); setSearchQuery(''); }}
                className="w-full py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export { SecteurSelectorModal, SecteurSelectorDropdown };
